import { supabase } from '@/integrations/supabase/client';
import { UserRole } from '@/lib/types';

// Add a user to company by updating their profile
export const addUserToCompany = async (userId: string, companyId: string, role: UserRole = 'member'): Promise<boolean> => {
  try {
    // First check if the user already has a role we need to preserve
    const { data: existingUser, error: fetchError } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .single();
    
    if (fetchError) {
      console.error('Error fetching user role:', fetchError);
    }
    
    // If no role is provided and user has an existing role, use that one
    const userRole = role || (existingUser?.role as UserRole) || 'member';
    
    // Update the profile with the company ID and the preserved role
    const { error } = await supabase
      .from('profiles')
      .update({ 
        company_id: companyId,
        role: userRole
      })
      .eq('id', userId);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error adding user to company:', error);
    return false;
  }
};

// Remove user from company by setting their company_id to null in profiles
export const removeUserFromCompany = async (userId: string, companyId: string): Promise<boolean> => {
  try {
    // Get current user role before removing from company
    const { data: userData, error: userError } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .single();
    
    if (userError) throw userError;
    
    // Keep the current role when removing from company
    const currentRole = userData?.role as UserRole;
    
    const { error } = await supabase
      .from('profiles')
      .update({ 
        company_id: null,
        // Preserve their current role
        role: currentRole 
      })
      .eq('id', userId)
      .eq('company_id', companyId);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error removing user from company:', error);
    return false;
  }
};

// Update user's role in company by updating their profile
export const updateUserCompanyRole = async (userId: string, companyId: string, role: UserRole): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ role: role })
      .eq('id', userId)
      .eq('company_id', companyId);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating user role:', error);
    return false;
  }
};
